import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import resourcesApi from '../../utils/resourcesApi';
import { performAdminCheck } from '../../utils/adminUtils';
import LoadingScreen from '../../components/LoadingScreen';
import { useDropzone } from 'react-dropzone';
import SimpleMDE from 'react-simplemde-editor';
import 'easymde/dist/easymde.min.css';

const ResourceFormPage = () => {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [featuredImageUrl, setFeaturedImageUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // Memoize the editor options and onChange handler to prevent re-renders
  const editorOptions = React.useMemo(() => ({
    spellChecker: false,
    placeholder: "Write your content using Markdown...",
    status: false,
    autofocus: false,
    toolbar: [
      "bold", "italic", "heading", "|", 
      "quote", "unordered-list", "ordered-list", "|",
      "link", "image", "table", "|",
      "preview", "side-by-side", "fullscreen", "|",
      "guide"
    ],
    toolbarTips: true,
    autoDownloadFontAwesome: false,
    shortcuts: {
      "toggleBold": "Ctrl-B",
      "toggleItalic": "Ctrl-I",
      "drawLink": "Ctrl-K",
      "toggleHeadingSmaller": "Ctrl-H",
      "toggleHeadingBigger": "Shift-Ctrl-H",
      "cleanBlock": "Ctrl-L",
      "drawImage": "Ctrl-Alt-I",
      "toggleBlockquote": "Ctrl-Q",
      "toggleOrderedList": "Ctrl-Alt-L",
      "toggleUnorderedList": "Ctrl-L",
    }
  }), []);
  
  const handleContentChange = useCallback((value) => {
    setContent(value);
  }, []);
  
  // Check if user is admin
  const checkAdminStatus = useCallback(async () => {
    const redirectPath = isEditing ? `/resources/edit/${id}` : '/resources/new';
    await performAdminCheck(user, setIsAdmin, navigate, redirectPath);
  }, [user, navigate, id, isEditing]);
  
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const categoriesData = await resourcesApi.getCategories();
      
      setCategories(categoriesData);
      
      // Set default category if creating and categories exist
      if (!isEditing && categoriesData.length > 0 && !categoryId) {
        setCategoryId(categoriesData[0].id);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to load categories. Please try again later.');
    }
  }, [isEditing, categoryId]);
  
  // Fetch resource data if editing
  const fetchResource = useCallback(async () => {
    if (!isEditing) {
      setLoading(false);
      return;
    }
    
    try {
      // For editing, we need to get the resource by ID, not slug
      // We're using Supabase directly here since our API doesn't have a getResourceById method yet
      const { data, error } = await supabase
        .from('resources')
        .select(`
          *,
          resource_resource_tags (
            resource_tags (id, name)
          )
        `)
        .eq('id', id)
        .single();
        
      if (error) throw error;
      
      if (!data) {
        throw new Error('Resource not found');
      }
      
      // Set form values from resource data
      setTitle(data.title);
      setSlug(data.slug);
      setSummary(data.summary);
      setContent(data.content);
      setCategoryId(data.category_id);
      setIsPublished(data.is_published);
      setFeaturedImageUrl(data.featured_image_url || '');
      
      // Extract tags from the nested structure
      if (data.resource_resource_tags) {
        const extractedTags = data.resource_resource_tags.map(tag => tag.resource_tags.name);
        setTags(extractedTags);
      }
    } catch (error) {
      console.error('Error fetching resource:', error);
      setError('Failed to load resource data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [isEditing, id]);
  
  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);
  
  useEffect(() => {
    if (isAdmin) {
      fetchCategories();
      fetchResource();
    }
  }, [isAdmin, fetchCategories, fetchResource]);
  
  // Generate slug from title
  const generateSlug = useCallback((text) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Remove consecutive hyphens
      .trim();
  }, []);
  
  // Handle title change and auto-generate slug if not editing
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    
    if (!isEditing || slug === '') {
      setSlug(generateSlug(newTitle));
    }
  };
  
  // Handle adding a new tag
  const handleAddTag = (e) => {
    e.preventDefault();
    
    if (!newTag.trim()) return;
    
    if (!tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
    }
    
    setNewTag('');
  };
  
  // Handle removing a tag
  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };
  
  // Handle image upload
  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles[0]) return;
    
    const file = acceptedFiles[0];
    setFeaturedImage(file);
    
    // Show preview
    const reader = new FileReader();
    reader.onload = () => {
      setFeaturedImageUrl(reader.result);
    };
    reader.readAsDataURL(file);
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxFiles: 1
  });
  
  // Upload image to storage
  const uploadImage = async () => {
    if (!featuredImage) return null;
    
    setIsUploading(true);
    try {
      const fileExt = featuredImage.name.split('.').pop();
      const filePath = `resource-images/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      // Use pitch_deck_assets bucket instead of 'public' which doesn't exist
      const { error: uploadError } = await supabase.storage
        .from('pitch_deck_assets')
        .upload(filePath, featuredImage, {
          cacheControl: '3600',
          upsert: false,
          onUploadProgress: (progress) => {
            setUploadProgress(Math.round((progress.loaded / progress.total) * 100));
          }
        });
        
      if (uploadError) throw uploadError;
      
      // Get public URL from the correct bucket
      const { data: publicUrlData } = supabase.storage
        .from('pitch_deck_assets')
        .getPublicUrl(filePath);
        
      return publicUrlData.publicUrl; // Note: it's publicUrl, not publicURL
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !slug || !summary || !content || !categoryId) {
      setError('Please fill all required fields');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      // Upload image if there's a new one
      let imageUrl = featuredImageUrl;
      if (featuredImage) {
        imageUrl = await uploadImage();
      }
      
      const resourceData = {
        title,
        slug,
        summary,
        content,
        category_id: categoryId,
        is_published: isPublished,
        featured_image_url: imageUrl,
        user_id: user.id
      };
      
      if (isEditing) {
        // Update resource using resourcesApi
        await resourcesApi.updateResource(id, resourceData, tags);
      } else {
        // Create resource using resourcesApi
        await resourcesApi.createResource(resourceData, tags);
      }
      
      // Navigate to resource page on success
      navigate(`/resources/${slug}`);
    } catch (error) {
      console.error('Error saving resource:', error);
      setError('Failed to save resource. Please try again later.');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return <LoadingScreen message={isEditing ? "Loading resource..." : "Loading form..."} />;
  }
  
  if (!isAdmin) {
    return <LoadingScreen message="Checking permissions..." />;
  }
  
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Edit Resource' : 'Create Resource'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing
            ? 'Update resource information'
            : 'Create a new resource for the biotech knowledge base'}
        </p>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Title */}
            <div className="col-span-2">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={handleTitleChange}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                required
              />
            </div>
            
            {/* Slug */}
            <div>
              <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
                Slug <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  /resources/
                </span>
                <input
                  id="slug"
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  className="flex-1 block w-full border-gray-300 rounded-none rounded-r-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  pattern="[a-z0-9\-]+"
                  title="Lowercase letters, numbers, and hyphens only"
                  required
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">URL-friendly name (letters, numbers, hyphens)</p>
            </div>
            
            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                required
              >
                <option value="">Select Category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Tags */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map(tag => (
                  <span 
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800"
                  >
                    {tag}
                    <button
                      type="button"
                      className="ml-1 text-primary-600 hover:text-primary-900 focus:outline-none"
                      onClick={() => handleRemoveTag(tag)}
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add a tag..."
                  className="flex-1 block w-full border-gray-300 rounded-l-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-r-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Add
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">Press "Add" to add a tag</p>
            </div>
            
            {/* Summary */}
            <div className="col-span-2">
              <label htmlFor="summary" className="block text-sm font-medium text-gray-700 mb-1">
                Summary <span className="text-red-500">*</span>
              </label>
              <textarea
                id="summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                required
              />
              <p className="mt-1 text-xs text-gray-500">Brief description displayed in search results and previews</p>
            </div>
            
            {/* Featured Image */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Featured Image</label>
              <div
                {...getRootProps()}
                className={`mt-1 p-6 border-2 border-dashed rounded-md ${
                  isDragActive 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-300 bg-gray-50'
                }`}
              >
                <input {...getInputProps()} />
                {featuredImageUrl ? (
                  <div className="space-y-2">
                    <div className="flex justify-center">
                      <img 
                        src={featuredImageUrl} 
                        alt="Preview" 
                        className="h-48 object-contain" 
                      />
                    </div>
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFeaturedImage(null);
                          setFeaturedImageUrl('');
                        }}
                        className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                      >
                        Remove Image
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <p className="text-sm text-gray-500">
                      {isDragActive
                        ? "Drop the image here"
                        : "Drag and drop an image, or click to select"}
                    </p>
                  </div>
                )}
                
                {isUploading && (
                  <div className="mt-4">
                    <div className="h-2 bg-gray-200 rounded-full">
                      <div
                        className="h-2 bg-primary-600 rounded-full"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="mt-1 text-xs text-center text-gray-500">
                      Uploading: {uploadProgress}%
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Content (with Markdown editor) */}
            <div className="col-span-2">
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                Content <span className="text-red-500">*</span>
              </label>
              <SimpleMDE
                id="content-editor"
                key="content-editor-instance"
                className="content-editor"
                value={content}
                onChange={handleContentChange}
                options={editorOptions}
              />
              <p className="mt-1 text-xs text-gray-500">
                Use Markdown for formatting. You can add headers, lists, links, images, and more.
              </p>
            </div>
            
            {/* Publish Status */}
            <div className="col-span-2">
              <div className="flex items-center">
                <input
                  id="published"
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="published" className="ml-2 block text-sm text-gray-700">
                  Publish Immediately
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                If unchecked, the resource will be saved as a draft and won't be visible to users.
              </p>
            </div>
          </div>
        </div>
        
        {/* Form actions */}
        <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
          <Link
            to="/resources/manage"
            className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving || isUploading}
            className={`px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
              (saving || isUploading) ? 'opacity-75 cursor-not-allowed' : ''
            }`}
          >
            {saving ? 'Saving...' : isEditing ? 'Update Resource' : 'Create Resource'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResourceFormPage; 